import { useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useLang } from '../i18n'
import { api, isApiError } from '../lib/api'
import { navigate, homePath } from '../lib/router'
import {
  DEFAULT_RETENTION_POLICY,
  keepCount,
  keepKind,
  makeKeep,
  parseWhole,
  RETENTION_KEEP_KINDS,
  RETENTION_MAX_AGE,
  RETENTION_MAX_COUNT,
  RETENTION_MAX_TIERS,
  RETENTION_UNITS,
  retentionPolicyError,
  type RetentionKeepKind,
  type RetentionPolicy,
  type RetentionUnit,
} from '../lib/retention'

/** One tier as the form holds it: the numbers stay text until Save parses them. */
interface TierDraft {
  n: string
  unit: RetentionUnit
  kind: RetentionKeepKind
  count: string
}

interface Draft {
  tiers: TierDraft[]
  start: string
  end: string
  timeZone: string
}

function draftOf(policy: RetentionPolicy): Draft {
  return {
    tiers: policy.tiers.map((tier) => ({
      n: String(tier.after.n),
      unit: tier.after.unit,
      kind: keepKind(tier.keep),
      count: String(keepCount(tier.keep) ?? 1),
    })),
    start: policy.dayWindow.start,
    end: policy.dayWindow.end,
    timeZone: policy.timeZone,
  }
}

/** The draft as a policy; an unparsable number becomes NaN, which the validation then refuses. */
function policyOf(draft: Draft): RetentionPolicy {
  return {
    tiers: draft.tiers.map((tier) => ({
      after: { n: parseWhole(tier.n), unit: tier.unit },
      keep: makeKeep(tier.kind, parseWhole(tier.count)),
    })),
    dayWindow: { start: draft.start, end: draft.end },
    timeZone: draft.timeZone.trim(),
  }
}

/**
 * The tenant's version-retention defaults (AB#245) over `/v1/admin/retention` (AB#244), reached from
 * the account menu's "Version retention", next to "Corporate templates" — a tenant admin's page.
 * Anyone else gets a "not allowed" message, and the backend refuses regardless.
 *
 * Every tier is a table row: its age (a number + a unit, hours to months), what it keeps (every
 * version, collapse runs, N per day / per week / in total) and the default N a bid admin may then
 * change for their bid. Below, the day window "N per day" slices and the time zone it is read in.
 * The form refuses what the API would (lib/retention.ts#retentionPolicyError, mirroring the domain)
 * before sending; the server checks again. "Restore the built-in defaults" only fills the form —
 * nothing changes until Save.
 */
export function VersionRetentionPage() {
  const { t, lang } = useLang()
  const qc = useQueryClient()
  const ids = useId()
  const headingRef = useRef<HTMLHeadingElement>(null)
  const me = useQuery({ queryKey: ['me'], queryFn: () => api.me(), staleTime: 30_000 })
  const isTenantAdmin = me.data?.roles?.includes('admin') ?? false

  const settings = useQuery({
    queryKey: ['retention', 'tenant'],
    queryFn: () => api.tenantRetention(),
    enabled: isTenantAdmin,
  })
  const [draft, setDraft] = useState<Draft | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [status, setStatus] = useState('')

  useEffect(() => {
    headingRef.current?.focus()
  }, [])

  useEffect(() => {
    if (settings.data) setDraft(draftOf(settings.data.policy))
  }, [settings.data])

  const header = (
    <div className="home-hero">
      <div className="home-hero-in">
        <button className="rel-back" onClick={() => navigate(homePath)}>
          ← {t('all_bids')}
        </button>
        <h1 ref={headingRef} tabIndex={-1}>
          {t('retention_admin_title')}
        </h1>
        {isTenantAdmin && <p>{t('retention_admin_intro')}</p>}
      </div>
    </div>
  )

  const shell = (body: ReactNode) => (
    <main className="homepage" aria-label={t('retention_admin_title')}>
      {header}
      <div className="retention-page">{body}</div>
    </main>
  )

  if (!me.data) return shell(<p aria-live="polite">…</p>)
  if (!isTenantAdmin) return shell(<p className="retention-readonly">{t('retention_admin_not_allowed')}</p>)
  if (settings.isError) {
    return shell(
      <p className="retention-error" role="alert">
        {t('retention_load_failed')}
      </p>,
    )
  }
  if (!draft || !settings.data) return shell(<p aria-live="polite">…</p>)

  const view = settings.data
  const setTier = (i: number, patch: Partial<TierDraft>): void =>
    setDraft((d) => d && { ...d, tiers: d.tiers.map((tier, j) => (j === i ? { ...tier, ...patch } : tier)) })

  function addTier(): void {
    setDraft((d) => {
      if (!d) return d
      const last = d.tiers[d.tiers.length - 1]
      const next: TierDraft = last
        ? { n: String(parseWhole(last.n) + 1 || 1), unit: last.unit, kind: 'total', count: '1' }
        : { n: '1', unit: 'weeks', kind: 'total', count: '1' }
      return { ...d, tiers: [...d.tiers, next] }
    })
  }

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault()
    if (!draft) return
    const policy = policyOf(draft)
    const why = retentionPolicyError(policy)
    setStatus('')
    if (why) {
      setError(t(why))
      return
    }
    setSaving(true)
    setError('')
    try {
      const next = await api.setTenantRetention(policy)
      qc.setQueryData(['retention', 'tenant'], next)
      // Every bid's merged policy and preview follow the tenant's.
      await qc.invalidateQueries({ queryKey: ['retention'] })
      await qc.invalidateQueries({ queryKey: ['retention-preview'] })
      setStatus(t('retention_saved'))
    } catch (err) {
      setError(isApiError(err) && err.serverMessage ? err.serverMessage : t('retention_save_failed'))
    } finally {
      setSaving(false)
    }
  }

  const when = view.updatedAt ? new Date(view.updatedAt).toLocaleString(lang === 'fr' ? 'fr-FR' : 'en-GB') : ''

  return shell(
    <form onSubmit={(e) => void submit(e)} aria-busy={saving}>
      <p className="visually-hidden" role="status" aria-live="polite">
        {status}
      </p>
      <p className="retention-hint">{t('retention_tagged_note')}</p>
      <p className="retention-hint">
        {view.isDefault
          ? t('retention_is_builtin')
          : t('retention_stamp')
              .replace('{who}', view.updatedBy ?? '—')
              .replace('{when}', when)}
      </p>

      <div className="retention-scroll">
        <table className="kbtable retention-table retention-admin-table">
          <caption className="visually-hidden">{t('retention_caption')}</caption>
          <thead>
            <tr>
              <th scope="col">{t('retention_col_age')}</th>
              <th scope="col">{t('retention_col_keep')}</th>
              <th scope="col">{t('retention_col_actions')}</th>
            </tr>
          </thead>
          <tbody>
            {draft.tiers.map((tier, i) => {
              const label = (key: Parameters<typeof t>[0]): string => t(key).replace('{i}', String(i + 1))
              const counted = tier.kind !== 'all' && tier.kind !== 'runs'
              return (
                <tr key={i}>
                  <td>
                    <span className="retention-fields">
                      <span aria-hidden="true">{t('retention_age_after').replace('{age}', '').trim()}</span>
                      <input
                        type="number"
                        min={0}
                        max={RETENTION_MAX_AGE}
                        step={1}
                        inputMode="numeric"
                        aria-label={label('retention_age_label')}
                        value={tier.n}
                        disabled={saving}
                        onChange={(e) => setTier(i, { n: e.target.value })}
                      />
                      <select
                        aria-label={label('retention_unit_label')}
                        value={tier.unit}
                        disabled={saving}
                        onChange={(e) => setTier(i, { unit: e.target.value as RetentionUnit })}
                      >
                        {RETENTION_UNITS.map((u) => (
                          <option key={u} value={u}>
                            {t(`retention_unit_${u}`)}
                          </option>
                        ))}
                      </select>
                    </span>
                  </td>
                  <td>
                    <span className="retention-fields">
                      <select
                        aria-label={label('retention_kind_label')}
                        value={tier.kind}
                        disabled={saving}
                        onChange={(e) => setTier(i, { kind: e.target.value as RetentionKeepKind })}
                      >
                        {RETENTION_KEEP_KINDS.map((k) => (
                          <option key={k} value={k}>
                            {t(`retention_kind_${k}`)}
                          </option>
                        ))}
                      </select>
                      {counted && (
                        <input
                          type="number"
                          min={1}
                          max={RETENTION_MAX_COUNT}
                          step={1}
                          inputMode="numeric"
                          aria-label={label('retention_tier_count_label')}
                          value={tier.count}
                          disabled={saving}
                          onChange={(e) => setTier(i, { count: e.target.value })}
                        />
                      )}
                    </span>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="mini no"
                      disabled={saving || draft.tiers.length <= 1}
                      onClick={() => setDraft((d) => d && { ...d, tiers: d.tiers.filter((_, j) => j !== i) })}
                    >
                      {label('retention_remove_tier')}
                    </button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <div className="retention-actions">
        <button
          type="button"
          className="mini no"
          disabled={saving || draft.tiers.length >= RETENTION_MAX_TIERS}
          onClick={addTier}
        >
          {t('retention_add_tier')}
        </button>
      </div>

      <fieldset className="retention-window">
        <legend>{t('retention_window_legend')}</legend>
        <p className="retention-hint" id={`${ids}-window-hint`}>
          {t('retention_window_hint')}
        </p>
        <div className="retention-window-fields">
          <label className="retention-field">
            <span>{t('retention_window_start')}</span>
            <input
              type="time"
              value={draft.start}
              aria-describedby={`${ids}-window-hint`}
              disabled={saving}
              onChange={(e) => setDraft((d) => d && { ...d, start: e.target.value })}
            />
          </label>
          <label className="retention-field">
            <span>{t('retention_window_end')}</span>
            <input
              type="time"
              value={draft.end}
              aria-describedby={`${ids}-window-hint`}
              disabled={saving}
              onChange={(e) => setDraft((d) => d && { ...d, end: e.target.value })}
            />
          </label>
          <label className="retention-field">
            <span>{t('retention_zone')}</span>
            <input
              type="text"
              value={draft.timeZone}
              spellCheck={false}
              autoComplete="off"
              disabled={saving}
              onChange={(e) => setDraft((d) => d && { ...d, timeZone: e.target.value })}
            />
          </label>
        </div>
      </fieldset>

      {error && (
        <p className="retention-error" role="alert">
          {error}
        </p>
      )}

      <div className="retention-actions">
        <button type="submit" className="mini ok" disabled={saving}>
          {t('retention_save')}
        </button>
        <button
          type="button"
          className="mini no"
          disabled={saving}
          onClick={() => {
            setError('')
            setDraft(draftOf(DEFAULT_RETENTION_POLICY))
          }}
        >
          {t('retention_builtin_reset')}
        </button>
      </div>
    </form>,
  )
}
